import React, { useLayoutEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography } from '@material-ui/core';
import Fab from '@material-ui/core/Fab';
import MicIcon from '@material-ui/icons/Mic';
import MicOffIcon from '@material-ui/icons/MicOff';
import VideocamIcon from '@material-ui/icons/Videocam';
import VideocamOffIcon from '@material-ui/icons/VideocamOff';
import CallEndIcon from '@material-ui/icons/CallEnd';
import LockOpenIcon from '@material-ui/icons/LockOpen';

const CallScreen = ({
  residentName,
  localStream,
  remoteStream,
  localTrackVideoDimensions,
  remoteTrackVideoDimensions,
  isReadyToStreamIN,
  isReadyToStreamOUT,
  isRinging,
  canUnlock,
  audioEnabled,
  videoEnabled,
  onUnlock,
  onHangUp,
  onToggleAudio,
  onToggleVideo,
  // history,
}) => {
  const classes = useStyles();

  const [localVideoDimensions, setLocalVideoDimensions] = useState({ width: '100%', height: '100%' });
  const [remoteVideoDimensions, setRemoteVideoDimensions] = useState({ width: '100%', height: '100%' });

  useLayoutEffect(() => {
    const localStreamContainerDimensions = elementDimensionsById('local-stream-container');
    const remoteStreamContainerDimensions = elementDimensionsById('remote-stream-container');

    if (localStreamContainerDimensions && localTrackVideoDimensions) {
      const lvd = scaleTrackDimensions(localStreamContainerDimensions, localTrackVideoDimensions, 1);
      console.log('local', localStreamContainerDimensions, localTrackVideoDimensions, lvd);
      setLocalVideoDimensions(lvd);
    }

    if (remoteStreamContainerDimensions && remoteTrackVideoDimensions) {
      const rvd = scaleTrackDimensions(remoteStreamContainerDimensions, remoteTrackVideoDimensions, 1);
      console.log('remote', remoteStreamContainerDimensions, remoteTrackVideoDimensions, rvd);
      setRemoteVideoDimensions(rvd);
    }
  }, [localTrackVideoDimensions, remoteTrackVideoDimensions]);

  return (
    <div className={classes.root}>
      <Box height="100%">
        <Box id="remote-stream-container" className={classes.remoteStreamContainer}>
          <video
            autoPlay={isReadyToStreamIN}
            playsInline={isReadyToStreamIN}
            height={remoteVideoDimensions.height}
            width={remoteVideoDimensions.width}
            ref={(video) => {
              if (video && remoteStream instanceof MediaStream) {
                video.srcObject = remoteStream;
              }
            }}
          />
        </Box>
        <Box id="local-stream-container" className={classes.localStreamContainer}>
          <video
            autoPlay={isReadyToStreamOUT}
            playsInline={isReadyToStreamOUT}
            height={localVideoDimensions.height}
            width={localVideoDimensions.width}
            ref={(video) => {
              if (video && localStream instanceof MediaStream) {
                // NOTE: we keep only the video stream because we
                // don't want the hear our own audio in a feedback
                // loop
                video.srcObject = new MediaStream(localStream.getVideoTracks());
              }
            }}
          />
        </Box>
        {isRinging && <Message classes={classes} name={residentName} action="calling..." />}
        <Box className={classes.dashboardContainer}>
          <UnlockDoorButton classes={classes} disabled={!canUnlock} onClick={onUnlock} />
          <HangUpButton classes={classes} disabled={false} onClick={onHangUp} />
          <ToggleAudioButton classes={classes} disabled={isRinging} active={audioEnabled} onClick={onToggleAudio} />
          <ToggleVideoButton classes={classes} disabled={isRinging} active={videoEnabled} onClick={onToggleVideo} />
        </Box>
      </Box>
    </div>
  );
};

const Message = ({ name, action, classes }) => (
  <Box className={classes.messageContainer}>
    <Typography color="textPrimary" variant="h4">
      {name}
    </Typography>
    <Typography className={classes.messageAction} color="textSecondary" variant="subtitle1">
      {action}
    </Typography>
  </Box>
);

const Button = ({ children, backgroundColor, classes, disabled, onClick }) => (
  <Fab disabled={disabled}
    style={{
      backgroundColor: disabled ? "#3D3D3D" : backgroundColor,
      opacity: disabled ? 0.3 : 1 }}
    className={classes.dashboardButton} onClick={onClick}
  >
    {children}
  </Fab>
);

const ToggleVideoButton = ({ active, ...props }) => (
  <Button {...props} backgroundColor="#3D3D3D">
    {active ? <VideocamIcon /> : <VideocamOffIcon />}
  </Button>
);

const ToggleAudioButton = ({ active, ...props }) => (
  <Button {...props} backgroundColor="#3D3D3D">
    {active ? <MicIcon /> : <MicOffIcon />}
  </Button>
);

const HangUpButton = ({ active, ...props }) => (
  <Button {...props} backgroundColor="#F24707">
    <CallEndIcon />
  </Button>
);

const UnlockDoorButton = ({ active, ...props }) => (
  <Button {...props} backgroundColor="#74F21F">
    <LockOpenIcon />
  </Button>
);

const elementDimensionsById = (id) => {
  const el = document.getElementById(id);
  return el && { width: el.clientWidth, height: el.clientHeight };
};

const scaleTrackDimensions = (containerDimensions, trackDimensions, factor) => {
  let height = trackDimensions.height;
  let width = trackDimensions.width;
  const aspect = width / height;

  height = containerDimensions.height;
  width = height * aspect;
  if (width < containerDimensions.width || height < containerDimensions.height) {
    width = containerDimensions.width;
    height = width / aspect;
  }
  return {
    height: Math.round(height * factor),
    width: Math.round(width * factor),
  };
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100vh',
    height: '100vh',
    backgroundColor: theme.palette.background.paper,
  },
  remoteStreamContainer: {
    zIndex: 0,
    position: 'absolute',
    top: '0px',
    left: '0px',
    right: '0px',
    bottom: '0px',
    background: '#C3C3C3',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  localStreamContainer: {
    zIndex: 1,
    position: 'absolute',
    top: '20px',
    right: '20px',
    height: '133px',
    width: '90px',
    background: '#3D3D3D',
    borderRadius: '10px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  dashboardContainer: {
    zIndex: 1,
    position: 'absolute',
    bottom: '20px',
    right: '20px',
    height: '60%',
    width: '90px',
    overflow: 'none',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  dashboardButton: {
    marginBottom: '10px',
    backgroundColor: '#3D3D3D',
    background: '#3D3D3D',
    color: 'white',
    // TODO: calculate based on container dimension
    // https://stackoverflow.com/questions/44480053/how-to-detect-if-screen-size-has-changed-to-mobile-in-react
    width: '70px',
    height: '70px',
  },
  messageContainer: { position: 'absolute', left: '20px', top: '20px', width: '60%' },
  messageAction: { marginTop: '-10px' },
}));

export default CallScreen;
